import Dexie, { type EntityTable, type Table } from 'dexie'
import { COVER_THUMBNAIL_WIDTH, type Bookmark, type ReadingProgress, type Comic, type PageMeta, type Category, type ComicCategory, type CloudSyncState } from '@tanko/shared'
import type { PublicationSnapshot, SourceFileSnapshot } from '../lib/upload-session'
import type { BackupQueueEntry } from '../lib/backup-queue-entry'
import { initialSyncState } from '../lib/comic-sync'

/** root：底下的直接子目錄是候選作品。comic：這個資料夾本身就是一本作品。 */
export type LibraryKind = 'root' | 'comic'

export interface Library {
  id: string
  name: string
  kind: LibraryKind
  handle: FileSystemDirectoryHandle
  addedAt: number
}

/** IndexedDB 不能索引 boolean，因此旗標一律以 0 或 1 儲存。 */
export type StoredFlag = 0 | 1
/**
 * 已註冊的作品以雲端目錄項為閱讀狀態的真相來源，本地這一份同時是快取與「還沒送出去」
 * 的紀錄。needsPush 為 1 代表這一筆尚未被伺服器受理，補送時就是查這個索引。
 */
export interface StoredProgress extends ReadingProgress { comicId: string; needsPush: StoredFlag }
/**
 * deleted 是本地的 tombstone。刪除若直接移除該列而推送當下失敗，這次刪除就沒有任何
 * 紀錄，下次載入會把它從雲端讀回來——使用者看到刪掉的書籤復活。
 */
export interface StoredBookmark extends Bookmark { comicId: string; needsPush: StoredFlag; deleted: StoredFlag }
/** width 用來判斷既有縮圖是否小於目前要求的封面寬度。 */
export interface CoverThumbnail { comicId: string; page: number; blob: Blob; width: number }
export interface LocalUploadSession {
  comicId: string
  uploadId: string
  pageCount: number
  sourceManifest: SourceFileSnapshot[]
  publication?: PublicationSnapshot
  createdAt: number
  updatedAt: number
}

/** 集合的鏡像。真相在雲端，這裡只在離線或登入過期時用來把成員折進集合格。 */
export interface StoredCollection { id: string; title: string; mode: 'series' | 'merged'; coverComicId: string | null; createdAt: number; updatedAt: number }
/** 成員鏡像連標題、頁數、封面頁、狀態與進度一起存：離線時集合格才畫得出封面與已讀本數。 */
export interface StoredCollectionMember {
  collectionId: string; comicId: string; position: number; title: string; pageCount: number; coverPage: number
  syncState: CloudSyncState; progress: { page: number; updatedAt: number } | null
}
export interface StoredCollectionCategory { collectionId: string; categoryId: string }

export const db = new Dexie('tanko') as Dexie & {
  libraries: EntityTable<Library, 'id'>
  comics: EntityTable<Comic, 'id'>
  pageMeta: Table<PageMeta, [string, number]>
  categories: EntityTable<Category, 'id'>
  comicCategories: Table<ComicCategory, [string, string]>
  readingProgress: EntityTable<StoredProgress, 'comicId'>
  bookmarks: Table<StoredBookmark, [string, string]>
  coverThumbnails: EntityTable<CoverThumbnail, 'comicId'>
  uploadSessions: EntityTable<LocalUploadSession, 'comicId'>
  backupQueue: EntityTable<BackupQueueEntry, 'comicId'>
  collections: EntityTable<StoredCollection, 'id'>
  collectionMembers: Table<StoredCollectionMember, [string, string]>
  collectionCategories: Table<StoredCollectionCategory, [string, string]>
}

db.version(1).stores({
  libraries: 'id',
  comics: 'id, libraryId, &[libraryId+relPath], addedAt',
  pageMeta: '[comicId+page], comicId',
  categories: 'id, sortOrder',
  comicCategories: '[comicId+categoryId], comicId, categoryId',
})

db.version(2).stores({
  readingProgress: 'comicId',
  bookmarks: '[comicId+id], comicId, [comicId+createdAt]',
  coverThumbnails: 'comicId',
})

db.version(3).stores({ uploadSessions: 'comicId, &uploadId, updatedAt' })

// 多來源：既有那一筆補上 kind 為 root，識別碼不動，作品的 id 因此保持有效。
db.version(4).stores({ libraries: 'id, kind, addedAt' }).upgrade(async tx => {
  await tx.table<Library>('libraries').toCollection().modify(library => {
    library.kind ??= 'root'
  })
})

// 封面縮圖放大到 350px：小於這個寬度的既有縮圖直接移除，該作品的卡片改為
// 顯示 coverPage 那一頁的原圖，與從未設定封面的作品行為一致。coverPage 不動。
db.version(5).stores({ coverThumbnails: 'comicId' }).upgrade(async tx => {
  await tx.table<CoverThumbnail>('coverThumbnails').toCollection()
    .filter(entry => !entry.width || entry.width < COVER_THUMBNAIL_WIDTH)
    .delete()
})

// 壓縮檔支援：既有作品全部來自資料夾，補上 kind 即可，識別碼不動。
db.version(6).stores({ comics: 'id, libraryId, &[libraryId+relPath], addedAt, kind' }).upgrade(async tx => {
  await tx.table<Comic>('comics').toCollection().modify(comic => {
    comic.kind ??= 'folder'
  })
})

// 目錄註冊：作品多一個相對於雲端目錄的狀態。既有作品以 cloudComicId 是否有值分流——
// 在這個版本之前，有值就代表已經發布完成，因此等同於 ready。
db.version(7).stores({ comics: 'id, libraryId, &[libraryId+relPath], addedAt, kind, syncState' }).upgrade(async tx => {
  await tx.table<Comic>('comics').toCollection().modify(comic => {
    comic.syncState ??= initialSyncState(comic.cloudComicId)
  })
})

// 備份佇列：作品在這張表裡的狀態與失敗原因跨重新整理保留。既有資料不需要轉換——
// 佇列在書庫載入時依 syncState 自動補齊。
db.version(8).stores({ backupQueue: 'comicId, status, queuedAt' })

// 待推送標記：閱讀狀態改掛雲端目錄項之後，本地的進度與書籤需要記得自己送出去了沒有。
// 既有的每一筆一律標為待推送——它們正是先前被關在裝置裡的那批資料，第一次成功連線就
// 補上雲端。這不會覆寫較新的雲端狀態，伺服器的 last-write-wins 會拒絕比較舊的那些。
db.version(9).stores({
  readingProgress: 'comicId, needsPush',
  bookmarks: '[comicId+id], comicId, [comicId+createdAt], needsPush',
}).upgrade(async tx => {
  await tx.table<StoredProgress>('readingProgress').toCollection().modify(entry => { entry.needsPush = 1 })
  await tx.table<StoredBookmark>('bookmarks').toCollection().modify(entry => { entry.needsPush = 1; entry.deleted = 0 })
})

// 隱私分類：既有的每一個分類升級後都不是隱私分類，書庫的本數因此與升級前相同。
// 鏡像本來就會在下一次取得分類時整份重建，這個升級只是讓重建之前的那一刻也是對的。
db.version(10).stores({ categories: 'id, sortOrder' }).upgrade(async tx => {
  await tx.table<Category>('categories').toCollection().modify(category => { category.private ??= false })
})

// 集合鏡像：純新增三張表，既有資料不動；內容在下一次取得集合清單時整份重建。
db.version(11).stores({
  collections: 'id',
  collectionMembers: '[collectionId+comicId], comicId, collectionId',
  collectionCategories: '[collectionId+categoryId], collectionId',
})

let firstInitialization = false
db.on('populate', () => { firstInitialization = true })

export async function initializeDatabase(): Promise<void> {
  await db.open()
  if (firstInitialization) {
    firstInitialization = false
    // 持久化是最佳努力，不讓拒絕或不支援阻擋書庫啟動。
    try { await navigator.storage?.persist?.() } catch { /* 可繼續使用一般儲存空間。 */ }
  }
}
